import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  FilterOperator,
  SortDirection,
} from 'src/app/shared/enums/common.enum';
import { Filter, Sort } from 'src/app/shared/interfaces/common.interface';
import { environment } from 'src/environments/environment';
import {
  Order,
  OrderCheckout,
  OrderLines,
  TrackPackage,
} from '../interfaces/order.interface';
import { TopUp } from '../interfaces/topup.interface';

const apiUrl = environment.apiUrl + 'user/order';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  buyAgainOrder: BehaviorSubject<any> = new BehaviorSubject({
    shopName: null,
    orderLines: [],
  });
  orderStatusState: BehaviorSubject<any> = new BehaviorSubject({
    order: null,
    trackPackage: null,
  });

  constructor(private http: HttpClient, private datePipe: DatePipe) {}

  setOrderStatusState(order: Order, trackPackage: TrackPackage) {
    this.orderStatusState.next({ order: order, trackPackage: trackPackage });
  }

  getOrderStatusState() {
    return this.orderStatusState.asObservable();
  }

  setbuyAgainOrderState(shopName: string, orderLines: OrderLines[]) {
    this.buyAgainOrder.next({ shopName: shopName, orderLines: orderLines });
  }

  getbuyAgainOrderState() {
    return this.buyAgainOrder.asObservable();
  }

  getOrderCheckouts(
    temPerPage: number,
    currentPage: number = 1,
    filters: Filter[] = [
      { field: 'checkouts.status', operator: FilterOperator.equal, value: 0 },
    ],
    sorts: Sort[] = [{ field: 'id', direction: SortDirection.desc }]
  ) {
    return this.http
      .post(apiUrl + '/checkout', {
        item_per_page: temPerPage,
        current_page: currentPage,
        filters: filters,
        sorts: sorts,
      })
      .pipe(
        map((res: any) => {
          let checkouts: OrderCheckout[] = res.data.checkouts;
          checkouts.forEach((checkout: any) => {
            checkout = this.formatCheckoutDate(checkout);
            let paymentDeadlineDate = new Date(checkout.transaction_time);
            paymentDeadlineDate.setDate(paymentDeadlineDate.getDate() + 1);
            checkout.paymentDeadline = this.datePipe.transform(
              new Date(paymentDeadlineDate),
              'd MMM yyyy, HH:mm:ss'
            );
          });
          return { totalItems: res.data.total_items, checkouts: checkouts };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getOrderCheckout(id: any) {
    return this.http
      .get(apiUrl + '/show_checkout/' + id)
      .pipe(
        map((res: any) => {
          let checkout: OrderCheckout = res.data.checkout;
          checkout = this.formatCheckoutDate(checkout);
          let paymentDeadlineDate = new Date(checkout.transaction_time);
          paymentDeadlineDate.setDate(paymentDeadlineDate.getDate() + 1);
          checkout.paymentDeadline =
            this.datePipe.transform(
              new Date(paymentDeadlineDate),
              'd MMM yyyy, HH:mm:ss'
            ) || '';
          let topUp: TopUp = res.data.top_up;
          return { checkout: checkout, top_up: topUp };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getOrders(
    itemPerPage: number,
    currentPage: number = 1,
    dateFilter: any = null,
    filters: Filter[] = [],
    sorts: Sort[] = [{ field: 'id', direction: SortDirection.desc }]
  ) {
    return this.http
      .post(apiUrl + 's', {
        item_per_page: itemPerPage,
        current_page: currentPage,
        start_date: dateFilter ? dateFilter.start : null,
        end_date: dateFilter ? dateFilter.end : null,
        filters: filters,
        sorts: sorts,
      })
      .pipe(
        map((res: any) => {
          let orders: Order[] = res.data.orders;
          orders.forEach((order) => (order = this.formatOrderDate(order)));
          return { totalItems: res.data.total_items, orders: orders };
        })
      );
  }

  getOrderDetail(id: number) {
    return this.http.get(apiUrl + '/detail/' + id).pipe(
      map((res: any) => {
        let order: Order = res.data.order;
        order = this.formatOrderDate(order);
        let paymentDeadlineDate = new Date(order.created_date);
        paymentDeadlineDate.setDate(paymentDeadlineDate.getDate() + 1);
        order.paymentDeadline =
          this.datePipe.transform(
            new Date(paymentDeadlineDate),
            'd MMM yyyy, HH:mm:ss'
          ) || '';
        return order;
      })
    );
  }

  acceptOrder(orderId: any) {
    return this.http.post(apiUrl + '/accept/' + orderId, '').pipe(
      map((res: any) => {
        this.handleSuccess('Berhasil menerima order');
        return res;
      })
    );
  }

  reportOrder(body: any) {
    return this.http.post(apiUrl + '/report', body).pipe(
      map((res: any) => {
        this.handleSuccess('Berhasil melaporkan pesanan');
        return res;
      })
    );
  }

  private handleSuccess(message: string) {}

  private formatCheckoutDate(checkout: OrderCheckout) {
    if (checkout.transaction_time)
      checkout.formatted_transaction_time =
        this.datePipe.transform(
          new Date(checkout.transaction_time),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (checkout.cancel_date)
      checkout.formatted_cancel_date =
        this.datePipe.transform(
          new Date(checkout.cancel_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (checkout.orders) {
      checkout.orders.forEach((order: any) => {
        if (order.dispatch_date)
          order.formatted_dispatch_date = this.datePipe.transform(
            new Date(order.dispatch_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.delivered_date)
          order.formatted_delivered_date = this.datePipe.transform(
            new Date(order.delivered_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.settled_date)
          order.formatted_settled_date = this.datePipe.transform(
            new Date(order.settled_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.cancel_date)
          order.formatted_cancel_date = this.datePipe.transform(
            new Date(order.cancel_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.report_date)
          order.formatted_report_date = this.datePipe.transform(
            new Date(order.report_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.resolve_date)
          order.formatted_resolve_date = this.datePipe.transform(
            new Date(order.resolve_date),
            'd MMM yyyy, HH:mm'
          );
        if (order.checkout && order.checkout.transaction_time)
          order.checkout.formatted_transaction_time = this.datePipe.transform(
            new Date(order.checkout.transaction_time),
            'd MMM yyyy, HH:mm'
          );
        if (order.checkout && order.checkout.cancel_date)
          order.checkout.formatted_cancel_date = this.datePipe.transform(
            new Date(order.checkout.cancel_date),
            'd MMM yyyy, HH:mm'
          );
      });
    }
    return checkout;
  }

  private formatOrderDate(order: Order) {
    if (order.created_date)
      order.formatted_created_date =
        this.datePipe.transform(
          new Date(order.created_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.paid_date)
      order.formatted_paid_date =
        this.datePipe.transform(
          new Date(order.paid_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.dispatch_date)
      order.formatted_dispatch_date =
        this.datePipe.transform(
          new Date(order.dispatch_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.delivered_date)
      order.formatted_delivered_date =
        this.datePipe.transform(
          new Date(order.delivered_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.settled_date)
      order.formatted_settled_date =
        this.datePipe.transform(
          new Date(order.settled_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.cancel_date)
      order.formatted_cancel_date =
        this.datePipe.transform(
          new Date(order.cancel_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.report_date)
      order.formatted_report_date =
        this.datePipe.transform(
          new Date(order.report_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.resolve_date)
      order.formatted_resolve_date =
        this.datePipe.transform(
          new Date(order.resolve_date),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.checkout && order.checkout.transaction_time)
      order.checkout.formatted_transaction_time =
        this.datePipe.transform(
          new Date(order.checkout.transaction_time),
          'd MMM yyyy, HH:mm'
        ) || '';
    if (order.checkout && order.checkout.cancel_date)
      order.checkout.formatted_cancel_date =
        this.datePipe.transform(
          new Date(order.checkout.cancel_date),
          'd MMM yyyy, HH:mm'
        ) || '';

    return order;
  }

  translateOrderStatus(status: number) {
    if (status == 1) return 'Paid';
    if (status == 2) return 'Dispatched';
    if (status == 3) return 'Delivered';
    if (status == 4) return 'Settled';
    if (status == 5) return 'Cancelled';
    if (status == 6) return 'Reported';
    if (status == 7) return 'Resolved';
    if (status == 8) return 'Accepted';
    return 'Pending';
  }
}
